"use client";

import { Button } from "@ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@ui/components/card";
import { CreditCard, Check, Loader2, Coins, Gift, ShoppingCart } from "lucide-react";
import { useUnlockOptions, useUnlockWithCreditsMutation } from "../lib/api";
import { toast } from "sonner";
import { REPORT_UNLOCK_CREDITS } from "@repo/payments/client";
import { useRouter } from "next/navigation";
import { config } from "@repo/config";
import { useTranslations, useLocale } from "next-intl";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@shared/lib/api-client";

// Get AI Report Unlock price from config
function getAiReportUnlockPrice(locale: string): string {
  const aiReportPlan = config.payments.plans.aiReportUnlock;
  const price = aiReportPlan.prices?.[0];
  
  if (!price) {
    return "$15"; // Fallback
  }
  
  return Intl.NumberFormat(locale === "de" ? "de-DE" : "en-US", {
    style: "currency",
    currency: price.currency,
    minimumFractionDigits: 0,
  }).format(price.amount);
}

interface UnlockReportOptionsProps {
  reportPublicToken: string;
  organizationId?: string;
  onStripeCheckout: () => void;
  isCheckoutLoading?: boolean;
}

export function UnlockReportOptions({
  reportPublicToken,
  organizationId,
  onStripeCheckout,
  isCheckoutLoading = false,
}: UnlockReportOptionsProps) {
  const router = useRouter();
  const t = useTranslations();
  const locale = useLocale();
  const { data: options, isLoading } = useUnlockOptions(reportPublicToken, organizationId);
  const unlockMutation = useUnlockWithCreditsMutation();
  
  // Get formatted price from config
  const formattedPrice = getAiReportUnlockPrice(locale);
  
  // Get PAYG config for credit pricing
  const paygConfig = config.payments.plans.paygCredits;
  const pricePerCredit = paygConfig?.prices?.[0]?.amount ?? 1;

  // Mutation to create a credits checkout session for PAYG users
  const createCreditsCheckoutMutation = useMutation({
    mutationFn: async (credits: number) => {
      const effectiveOrgId = options?.organizationId || organizationId;
      if (!effectiveOrgId) {
        throw new Error("Organization not found");
      }

      const response = await apiClient.payments["create-credits-checkout"].$post({
        json: {
          organizationId: effectiveOrgId,
          credits,
          // Redirect back to the unlock page after purchase
          redirectUrl: `${window.location.origin}/reports/${reportPublicToken}/unlock`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to create checkout");
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to create checkout");
    },
  });

  const handleBuyCredits = () => {
    // Calculate credits needed: required credits minus available, with minimum of minPurchaseAmount
    const requiredCredits = options?.creditsRequired || REPORT_UNLOCK_CREDITS;
    const availableCredits = options?.creditsAvailable || 0;
    const creditsNeeded = Math.max(
      requiredCredits - availableCredits,
      paygConfig?.minPurchaseAmount ?? 15
    );
    createCreditsCheckoutMutation.mutate(creditsNeeded);
  };

  const handleUnlockWithCredits = async () => {
    // Use organizationId from options (backend-determined) or from prop
    const effectiveOrgId = options?.organizationId || organizationId;
    
    if (!effectiveOrgId) {
      toast.error("Organization not found");
      return;
    }

    try {
      await unlockMutation.mutateAsync({
        reportPublicToken,
        organizationId: effectiveOrgId,
      });
      toast.success("Report unlocked successfully!");
      // Redirect to the report
      router.push(`/reports/${reportPublicToken}`);
      router.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to unlock report");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!options) {
    return null;
  }

  // Already unlocked
  if (options.isAlreadyUnlocked) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            {t("marketing.aiCreditReport.unlockOptions.alreadyUnlocked.title")}
          </CardTitle>
          <CardDescription>
            {t("marketing.aiCreditReport.unlockOptions.alreadyUnlocked.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => router.push(`/reports/${reportPublicToken}`)}>
            {t("marketing.aiCreditReport.unlockOptions.alreadyUnlocked.viewReport")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Legacy plan - free unlock
  if (options.canUnlockWithLegacyPlan) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-purple-600" />
            {t("marketing.aiCreditReport.unlockOptions.legacyPlan.title")}
          </CardTitle>
          <CardDescription>
            {t("marketing.aiCreditReport.unlockOptions.legacyPlan.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleUnlockWithCredits}
            disabled={unlockMutation.isPending}
            size="lg"
            className="w-full"
          >
            {unlockMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("marketing.aiCreditReport.unlockOptions.unlocking")}
              </>
            ) : (
              <>
                <Gift className="mr-2 h-4 w-4" />
                {t("marketing.aiCreditReport.unlockOptions.legacyPlan.button")}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Credit-based unlock for organization members
  if (options.belongsToOrganization) {
    const availableCredits = options.creditsAvailable || 0;
    const requiredCredits = options.creditsRequired || REPORT_UNLOCK_CREDITS;
    const willCauseOverage = availableCredits < requiredCredits;
    const overageAmount = Math.max(0, requiredCredits - availableCredits);

    return (
      <div className="space-y-4">
        {/* Credit unlock option */}
        {options.canUnlockWithCredits && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-amber-600" />
                {t("marketing.aiCreditReport.unlockOptions.creditUnlock.title")}
              </CardTitle>
              <CardDescription>
                {willCauseOverage 
                  ? t("marketing.aiCreditReport.unlockOptions.creditUnlock.descriptionOverage", { required: requiredCredits, overage: overageAmount })
                  : t("marketing.aiCreditReport.unlockOptions.creditUnlock.description", { credits: requiredCredits })
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("marketing.aiCreditReport.unlockOptions.creditUnlock.availableCredits")}</span>
                <span className="font-semibold">{availableCredits}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("marketing.aiCreditReport.unlockOptions.creditUnlock.requiredCredits")}</span>
                <span className="font-semibold">{requiredCredits}</span>
              </div>
              {willCauseOverage && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <p className="text-sm text-amber-800">
                    {t("marketing.aiCreditReport.unlockOptions.creditUnlock.overageWarning", { overage: overageAmount })}
                  </p>
                </div>
              )}
              <Button
                onClick={handleUnlockWithCredits}
                disabled={unlockMutation.isPending}
                size="lg"
                className="w-full"
              >
                {unlockMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("marketing.aiCreditReport.unlockOptions.unlocking")}
                  </>
                ) : (
                  <>
                    <Coins className="mr-2 h-4 w-4" />
                    {willCauseOverage 
                      ? t("marketing.aiCreditReport.unlockOptions.creditUnlock.buttonOverage", { overage: overageAmount }) 
                      : t("marketing.aiCreditReport.unlockOptions.creditUnlock.button", { credits: requiredCredits })
                    }
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* PAYG users with insufficient credits - show purchase credits option */}
        {!options.canUnlockWithCredits && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-amber-600" />
                {t("marketing.aiCreditReport.unlockOptions.insufficientCredits.title")}
              </CardTitle>
              <CardDescription>
                {t("marketing.aiCreditReport.unlockOptions.insufficientCredits.description", { required: requiredCredits, available: availableCredits })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("marketing.aiCreditReport.unlockOptions.creditUnlock.availableCredits")}</span>
                <span className="font-semibold">{availableCredits}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("marketing.aiCreditReport.unlockOptions.creditUnlock.requiredCredits")}</span>
                <span className="font-semibold">{requiredCredits}</span>
              </div>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                <p className="text-sm text-blue-800">
                  {t("marketing.aiCreditReport.unlockOptions.insufficientCredits.buyCreditsInfo", { 
                    credits: Math.max(requiredCredits - availableCredits, paygConfig?.minPurchaseAmount ?? 15),
                    price: (Math.max(requiredCredits - availableCredits, paygConfig?.minPurchaseAmount ?? 15) * pricePerCredit).toFixed(0)
                  })}
                </p>
              </div>
              <Button
                onClick={handleBuyCredits}
                disabled={createCreditsCheckoutMutation.isPending}
                size="lg"
                className="w-full"
              >
                {createCreditsCheckoutMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("marketing.aiCreditReport.unlockOptions.processing")}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    {t("marketing.aiCreditReport.unlockOptions.insufficientCredits.buyCreditsButton", { 
                      credits: Math.max(requiredCredits - availableCredits, paygConfig?.minPurchaseAmount ?? 15)
                    })}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* One-time purchase option (only for guests without a plan) */}
        {options.canPurchaseOneTime && !options.belongsToOrganization && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {t("marketing.aiCreditReport.unlockOptions.oneTimePurchase.title")}
              </CardTitle>
              <CardDescription>
                {t("marketing.aiCreditReport.unlockOptions.oneTimePurchase.description", { price: formattedPrice })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={onStripeCheckout}
                disabled={isCheckoutLoading}
                variant="outline"
                size="lg"
                className="w-full"
              >
                {isCheckoutLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("marketing.aiCreditReport.unlockOptions.processing")}
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t("marketing.aiCreditReport.unlockOptions.oneTimePurchase.button", { price: formattedPrice })}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Guest user - only one-time purchase
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          {t("marketing.aiCreditReport.unlockOptions.guestPurchase.title")}
        </CardTitle>
        <CardDescription>
          {t("marketing.aiCreditReport.unlockOptions.guestPurchase.description", { price: formattedPrice })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          onClick={onStripeCheckout}
          disabled={isCheckoutLoading}
          size="lg"
          className="w-full"
        >
          {isCheckoutLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("marketing.aiCreditReport.unlockOptions.processing")}
            </>
          ) : (
            <>
              <CreditCard className="mr-2 h-4 w-4" />
              {t("marketing.aiCreditReport.unlockOptions.guestPurchase.button", { price: formattedPrice })}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

